import { localStore, readSetting, writeSetting, type SettingsStore } from "../settings.ts";
import type { ActionId } from "./actions.ts";
import { actionOfPress, formatBindings, parseBindings, sameBindings, type Bindings } from "./bindings.ts";
import { codeLabel, pageLayout, parseLayoutChoice, type KeyLayout, type LayoutChoice } from "./layout.ts";

/**
 * The page's key table (P1.9-T50; ADR-061's amendments "key rebinding", decisions 9 and 11, and "every key rebindable
 * but Escape", points 1, 7 and 8): the one table every key reader and every prompt that names a key reads, kept per
 * device with the player's Keyboard layout choice, and heard when Settings' Apply changes either or the browser's layout
 * map arrives.
 */

/**
 * The storage key the table is kept under.
 */
export const KEYS_KEY = "odyssey.keys";

/**
 * The storage key the Keyboard layout choice is kept under.
 */
export const LAYOUT_KEY = "odyssey.keys.layout";

/**
 * What a key reader or a prompt needs of the table.
 */
export interface KeyTable {
  /**
   * The physical key an action is bound to.
   *
   * @param action The action.
   * @returns Its `KeyboardEvent.code`.
   */
  code(action: ActionId): string;
  /**
   * The action a press is (`actionOfPress`): the action on its physical key, or on the key it is an alias of.
   *
   * @param press The press's `key` and, when the event says, its `code`.
   * @returns The action, or null when the press is none.
   */
  actionOf(press: { readonly key: string; readonly code?: string | undefined }): ActionId | null;
  /**
   * The label an action's key is shown by, by the layout in force.
   *
   * @param action The action.
   * @returns The label.
   */
  label(action: ActionId): string;
  /**
   * Listens for the table or the layout changing.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  subscribe(listener: () => void): () => void;
}

/**
 * A layout the table names keys by, which may say when it changes and take the player's choice.
 */
export type HeardLayout = KeyLayout & { subscribe?(listener: () => void): () => void; choose?(choice: LayoutChoice): void; readonly choice?: LayoutChoice };

/**
 * The table, read from the device's store as it is made and kept there on each set.
 */
export class KeyBindings implements KeyTable {
  private table: Bindings;
  private readonly listeners = new Set<() => void>();

  /**
   * @param store The device's store, null where there is none.
   * @param layout The layout keys are named by; the page's unless given.
   */
  public constructor(
    private readonly store: SettingsStore | null,
    public readonly layout: HeardLayout = pageLayout,
  ) {
    this.table = readSetting(KEYS_KEY, parseBindings, store);
    layout.choose?.(readSetting(LAYOUT_KEY, parseLayoutChoice, store));
    layout.subscribe?.(() => this.tell());
  }

  /**
   * The table in force.
   *
   * @returns The bindings.
   */
  public get bindings(): Bindings {
    return this.table;
  }

  /**
   * The Keyboard layout choice in force.
   *
   * @returns The choice; Auto for a layout that takes none.
   */
  public get layoutChoice(): LayoutChoice {
    return this.layout.choice ?? "auto";
  }

  /**
   * The physical key an action is bound to.
   *
   * @param action The action.
   * @returns Its code.
   */
  public code(action: ActionId): string {
    return this.table[action];
  }

  /**
   * The label an action's key is shown by.
   *
   * @param action The action.
   * @returns The label.
   */
  public label(action: ActionId): string {
    return codeLabel(this.table[action], this.layout);
  }

  /**
   * The action a press is.
   *
   * @param press The press's `key` and, when the event says, its `code`.
   * @returns The action, or null.
   */
  public actionOf(press: { readonly key: string; readonly code?: string | undefined }): ActionId | null {
    return actionOfPress(this.table, press);
  }

  /**
   * Puts a table in force and keeps it, telling every listener when it differs from the one in force.
   *
   * @param bindings The table.
   */
  public set(bindings: Bindings): void {
    if (sameBindings(this.table, bindings)) {
      return;
    }
    this.table = bindings;
    writeSetting(KEYS_KEY, formatBindings(bindings), this.store);
    this.tell();
  }

  /**
   * Puts a Keyboard layout choice in force and keeps it; the layout tells the listeners when it changes.
   *
   * @param choice The choice.
   */
  public setLayout(choice: LayoutChoice): void {
    if (choice === this.layoutChoice) {
      return;
    }
    writeSetting(LAYOUT_KEY, choice, this.store);
    this.layout.choose?.(choice);
  }

  /**
   * Tells every listener.
   */
  private tell(): void {
    for (const listener of [...this.listeners]) {
      listener();
    }
  }

  /**
   * Listens for the table or the layout changing.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
}

/**
 * The page's one key table.
 */
export const pageKeys = new KeyBindings(localStore());
